<template>
  <mia-form @submit="save">
    <mia-form-section title="Unverbindliche Preisempfehlung">
      <template #description>
        <mia-required-field-notice />
      </template>

      <mia-form-section-grid>
        <!-- Net List Price -->
        <div>
          <mia-input-with-addons>
            <template #default>
              <div class="flex-1">
                <mia-car-edit-feature-input
                  v-model="form.netListPrice"
                  :label="isDifferentialTax ? 'Streichpreis' : 'UPE Netto (ohne Garantie für Prämienberechnung)'"
                  required
                  input-class="text-right"
                  :readonly="!car.own_car"
                  :errors="getErrors('net_list_price')"
                  data-highlight="unverbindliche-preisempfehlung-upe"
                  @input="clearErrors('net_list_price')"
                />
              </div>
            </template>

            <template #right-addon>
              <div class="flex items-end">
                <div
                  class="flex items-center justify-center px-3 bg-gray-500 rounded-r"
                  style="height: 36px;"
                >
                  €
                </div>
              </div>
            </template>
          </mia-input-with-addons>
        </div>

        <!-- Net List Price At Ordertime -->
        <mia-owns-car>
          <div v-if="!isDifferentialTax">
            <mia-input-with-addons>
              <template #default>
                <div class="flex-1">
                  <mia-car-edit-feature-input
                    v-model="form.netListPriceAtOrderTime"
                    label="UPE zum Bestellzeitpunkt Netto"
                    input-class="text-right"
                    :errors="getErrors('net_list_price_at_order_time')"
                    data-highlight="unverbindliche-preisempfehlung-upe-bestellzeitpunkt"
                    @input="clearErrors('net_list_price_at_order_time')"
                  />
                </div>
              </template>

              <template #right-addon>
                <div class="flex items-end">
                  <div
                    class="flex items-center justify-center px-3 bg-gray-500 rounded-r"
                    style="height: 36px;"
                  >
                    €
                  </div>
                </div>
              </template>
            </mia-input-with-addons>
          </div>
        </mia-owns-car>

        <!-- Net List Price Invoice -->
        <div v-if="!isDifferentialTax">
          <mia-input-with-addons>
            <template #default>
              <div class="flex-1">
                <mia-car-edit-feature-input
                  v-model="form.netListPriceInvoice"
                  label="UPE aus Rechnung Netto"
                  input-class="text-right"
                  :readonly="!car.own_car"
                  :errors="getErrors('net_list_price_invoice')"
                  data-highlight="unverbindliche-preisempfehlung-upe-rechnung"
                  @input="clearErrors('net_list_price_invoice')"
                />
              </div>
            </template>

            <template #right-addon>
              <div class="flex items-end">
                <div
                  class="flex items-center justify-center px-3 bg-gray-500 rounded-r"
                  style="height: 36px;"
                >
                  €
                </div>
              </div>
            </template>
          </mia-input-with-addons>

          <div
            v-if="listPriceDifference < 0"
            class="mt-1 text-lg font-bold text-red-600"
          >
            {{ listPriceDifference | price }}
          </div>
        </div>

        <!-- Tax -->
        <div>
          <mia-car-edit-feature-select
            v-model="form.tax"
            label="Besteuerung"
            :errors="getErrors('tax')"
            :options="taxOptions"
            track-by="id"
            required
            name-by="label"
            data-highlight="unverbindliche-preisempfehlung-besteuerung"
            @input="clearErrors('tax')"
          />
        </div>
      </mia-form-section-grid>
    </mia-form-section>

    <template #actions>
      <div class="flex justify-between w-full">
        <mia-car-edit-section-progress
          page="prices"
          section="recommendedRetailPriceData"
        />

        <mia-owns-car>
          <mia-car-edit-submit-button
            v-if="car.item_closed === 'N'"
            :disabled="$wait.any"
            :is-loading="$wait.is('updating recommended retail price data')"
          />
          <mia-closed-hint v-else />
        </mia-owns-car>
      </div>
    </template>
  </mia-form>
</template>

<script>
import numeral from 'numeral'
import 'numeral/locales/de'
import { getCar } from '@/api/car'
import { updateRecommendedRetailPriceData } from '@/api/cars'
import Form from '@/components/ui/widgets/Form.vue'
import FormSection from '@/components/ui/widgets/FormSection.vue'
import FormSectionGrid from '@/components/ui/widgets/FormSectionGrid.vue'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import SectionProgress from '@/components/cars/edit/SectionProgress.vue'
import InputWithAddons from '@/components/ui/inputs/InputWithAddons.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import ClosedHint from '@/components/cars/ClosedHint.vue'
import RequiredFieldNotice from '@/components/cars/edit/forms/RequiredFieldNotice.vue'
import { cashSave } from '@/helpers/utils'
import taxOptions from '@/mia/tax/taxOptions'

numeral.locale('de')

export default {
  name: 'MiaCarEditRecommendedRetailPriceForm',

  components: {
    'mia-car-edit-feature-input': FeatureInput,
    'mia-car-edit-feature-select': FeatureSelect,
    'mia-car-edit-section-progress': SectionProgress,
    'mia-car-edit-submit-button': SubmitButton,
    'mia-closed-hint': ClosedHint,
    'mia-form-section-grid': FormSectionGrid,
    'mia-form-section': FormSection,
    'mia-form': Form,
    'mia-input-with-addons': InputWithAddons,
    'mia-required-field-notice': RequiredFieldNotice
  },

  mixins: [HandlesBackendValidationErrors],

  data() {
    return {
      form: {
        netListPrice: '',
        netListPriceAtOrderTime: '',
        netListPriceInvoice: '',
        tax: ''
      },
      taxOptions
    }
  },

  computed: {
    car: {
      get() {
        return this.$store.state.car.car
      },

      set(car) {
        this.$store.commit('setCar', car)
      }
    },

    isDifferentialTax() {
      return this.form.tax === 'differential'
    },

    listPriceDifference() {
      if (this.isDifferentialTax) {
        return 0
      }

      const netListPriceAtOrderTime = cashSave(this.form.netListPriceAtOrderTime)
      const netListPriceInvoice = cashSave(this.form.netListPriceInvoice)

      if (netListPriceAtOrderTime === 0 || netListPriceInvoice === 0) {
        return 0
      }

      return netListPriceAtOrderTime - netListPriceInvoice
    }
  },

  watch: {
    car: {
      immediate: true,
      deep: true,
      handler(car) {
        this.form.netListPrice = (`${car.item_net_list_price}`).replace('.', ',')
        this.form.netListPriceAtOrderTime = (`${car.item_net_list_price_at_ordertime}`).replace('.', ',')
        this.form.netListPriceInvoice = (`${car.item_net_list_price_invoice || ''}`).replace('.', ',')
        this.form.tax = car.item_tax
      }
    }
  },

  methods: {
    async save() {
      this.$wait.start('updating recommended retail price data')

      try {
        const payload = {
          net_list_price: numeral(this.form.netListPrice).value(),
          tax: this.form.tax
        }

        if (!this.isDifferentialTax) {
          payload.net_list_price_at_order_time = numeral(this.form.netListPriceAtOrderTime).value()
          payload.net_list_price_invoice = numeral(this.form.netListPriceInvoice).value()
        }

        await updateRecommendedRetailPriceData(this.car.id, payload)

        this.car = await getCar({
          carId: this.car.id
        })

        window.eventBus.$emit('mia-info', {
          info: ['Daten erfolgreich gespeichert.']
        })
      } catch (e) {
        this.handleBackendValidationErrors(e)

        window.eventBus.$emit('mia-error', {
          error: ['Fehler beim Speichern der Daten.']
        })
      } finally {
        this.$wait.end('updating recommended retail price data')
      }
    }
  }
}
</script>
